/**
 * FilterBar — template UI chuẩn cho Filter Area trên list page.
 *
 * Bao gồm:
 *   - <FilterBar>          : container ngang `Filter [buttons]   [rightSlot]`
 *   - <FilterButton>       : nút dropdown toggle popover, highlight khi active
 *   - filterBarBtnSx       : style chung cho button trong filter bar
 *
 * Pairs with useFilterPopover (state) + <FilterPopover>/<SearchFilterContent>.
 */
import { type MouseEvent, type ReactNode } from 'react'
import { Button, Stack, Typography } from '@mui/material'
import { alpha, type SxProps, type Theme } from '@mui/material/styles'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'

export const filterBarBtnSx: SxProps<Theme> = (theme) => ({
  minWidth: 0,
  px: 0.75,
  py: 0.2,
  minHeight: 26,
  textTransform: 'none',
  fontSize: '0.7rem',
  lineHeight: 1.1,
  '&.MuiButton-outlined': {
    borderColor: alpha(theme.palette.text.primary, 0.14),
    '&:hover': {
      borderColor: alpha(theme.palette.text.primary, 0.24),
      backgroundColor: alpha(theme.palette.text.primary, 0.04),
    },
  },
})

export interface FilterBarProps {
  /** Các <FilterButton> + custom controls (Columns menu...). */
  children: ReactNode
  /** True khi có ít nhất một filter đang active → render "Clear All". */
  hasActiveFilters?: boolean
  onClearAll?: () => void
  /** Optional — render "Clear All (N)" để chỉ rõ Clear All sẽ reset N filter. */
  activeFilterCount?: number
  /** Slot phải — toolbar action buttons (View / Edit / Print...). */
  rightSlot?: ReactNode
  /** Hiển thị nhãn "Filter" trước các nút. Bỏ khi caller muốn custom label. */
  showLabel?: boolean
}

export function FilterBar({
  children,
  hasActiveFilters,
  onClearAll,
  activeFilterCount,
  rightSlot,
  showLabel = true,
}: FilterBarProps) {
  return (
    <Stack
      direction="row"
      flexWrap="wrap"
      useFlexGap
      justifyContent="space-between"
      alignItems="center"
      sx={{ mb: 0.5 }}
    >
      <Stack
        direction="row"
        spacing={0.375}
        flexWrap="wrap"
        useFlexGap
        alignItems="center"
        sx={{ minWidth: 0 }}
      >
        {showLabel && (
          <Typography variant="caption" color="text.secondary" sx={{ mr: 0.25 }}>
            Filter
          </Typography>
        )}
        {children}
        {hasActiveFilters && onClearAll && (
          <Button
            size="small"
            variant="text"
            color="secondary"
            onClick={onClearAll}
            sx={filterBarBtnSx}
          >
            Clear All{activeFilterCount && activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
          </Button>
        )}
      </Stack>
      {rightSlot}
    </Stack>
  )
}

export interface FilterButtonProps {
  label: string
  /** True khi filter này đang có giá trị → đổi sang contained/primary. */
  active: boolean
  onClick: (event: MouseEvent<HTMLElement>) => void
  /** Disable khi page chưa load xong (optional). */
  disabled?: boolean
}

export function FilterButton({ label, active, onClick, disabled }: FilterButtonProps) {
  return (
    <Button
      size="small"
      variant={active ? 'contained' : 'outlined'}
      color={active ? 'primary' : 'inherit'}
      endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />}
      onClick={onClick}
      disabled={disabled}
      sx={filterBarBtnSx}
    >
      {label}
    </Button>
  )
}
